import Uppy, {type UppyOptions, type UppyFile} from '@uppy/core';
import deepmerge from 'deepmerge';
import ThumbnailGenerator, {
	type ThumbnailOptions,
} from '@uppy/thumbnail-generator';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import {invalidCharsPattern, invalidPeriodsPattern} from '../../constants';
import {
	UppyMultiDestination,
	UploaderType,
	type UppyMultiDestinationOptions,
} from '../../lib';
import {UploadMessage} from '../../locales';
import {UppyStateStore} from '../../store';
import type {UppyFileMeta} from '../../types';
import {getUnchunkedMaxFileSize} from '../getUnchunkedMaxFileSize';

type CreateUppyMultiDestinationInstanceOptions<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> = {
	store: TStore;
	multiDestinationOptions: UppyMultiDestinationOptions;
	uppyOptions?: Partial<Omit<UppyOptions<TMeta>, 'store'>>;
	useThumbnail?: boolean;
	thumnailOptions?: Partial<ThumbnailOptions>;
};

export const uppyOnBeforeFileAdded = (
	currentFile: UppyFile<Record<string, unknown>, Record<string, unknown>>,
) => {
	// eslint-disable-next-line security/detect-non-literal-regexp
	const charsRegex = new RegExp(invalidCharsPattern);
	// eslint-disable-next-line security/detect-non-literal-regexp
	const periodsRegex = new RegExp(invalidPeriodsPattern);

	// we do not allow uploading files that do not have an extension
	// or certain patterns/characters
	if (
		!currentFile.extension ||
		charsRegex.test(currentFile.name) ||
		!periodsRegex.test(currentFile.name)
	) {
		// uppy.log(`Skipping file because it has no extension`);
		// uppy.info(`Skipping file because it has no extension`, 'error', 500);
		dispatcher.trigger('app:snackbar', {
			message: UploadMessage.INVALID_FILE_NAMES,
			severity: 'error',
		});
		return false;
	}

	return true;
};

/**
 * This attempts to helpfully handle the creation of an uppy multi destination instance\
 * We have many quirks and to keep re-declaring them is exhausting\
 * Even if you are only using a single destination, this is probably the best util to use for future proofing
 * @param options overrides
 * @returns Uppy instance
 */
export const createUppyMultiDestinationInstance = <
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
>({
	multiDestinationOptions,
	uppyOptions,
	useThumbnail,
	store,
	thumnailOptions,
}: CreateUppyMultiDestinationInstanceOptions<TMeta>) => {
	const uppy = new Uppy<TStore>({
		store,
		autoProceed: false,
		// we do not want to allow multiple upload batches when using xhr
		allowMultipleUploadBatches: !multiDestinationOptions.useXHR,
		...(uppyOptions as any), // TODO: type error in the onBeforeUpload cb
		onBeforeFileAdded: uppyOnBeforeFileAdded,
		meta: {
			// this property is required by the multi destination plugin to determine the uploader
			// we have no use cases where xhr would allow for potentially picking tus or s3
			// we do have the ability selected between tus and s3 - but that is done in other interactions
			// it is safe to assume that if we are using xhr we are using xhr
			// otherwise we would be using tus or s3 and if not speicifed we would be using tus
			uploader: multiDestinationOptions.useXHR
				? UploaderType.XHR
				: UploaderType.TUS,
			...uppyOptions?.meta,
		},
		restrictions: {
			maxFileSize: multiDestinationOptions.useXHR
				? getUnchunkedMaxFileSize()
				: undefined,
			...uppyOptions?.restrictions,
		},
	});

	uppy.use(UppyMultiDestination, multiDestinationOptions);

	if (useThumbnail) {
		uppy.use(ThumbnailGenerator, {
			lazy: true,
			thumbnailWidth: 200,
			waitForThumbnailsBeforeUpload: false,
			...thumnailOptions,
		});
	}

	return uppy;
};

export default createUppyMultiDestinationInstance;
